import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  BookOpen, ChevronDown, ChevronRight, DollarSign, Calculator,
  Building2, Briefcase, Calendar, PiggyBank, TrendingUp, Shield,
  Lightbulb, Search, ArrowRight, Clock, Percent, Info
} from "lucide-react";

const formatCurrency = (v) => {
  if (v === null || v === undefined) return "No limit";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
};

const SECTION_ICONS = {
  brackets: Percent,
  standard_deduction: DollarSign,
  capital_gains: TrendingUp,
  credits: Shield,
  retirement: PiggyBank,
  deductions: Calculator,
  real_estate: Building2,
  self_employment: Briefcase,
  filing_deadlines: Calendar,
  strategies: Lightbulb,
};

const SECTION_COLORS = {
  brackets: "text-blue-400 bg-blue-500/10",
  standard_deduction: "text-emerald-400 bg-emerald-500/10",
  capital_gains: "text-purple-400 bg-purple-500/10",
  credits: "text-amber-400 bg-amber-500/10",
  retirement: "text-pink-400 bg-pink-500/10",
  deductions: "text-cyan-400 bg-cyan-500/10",
  real_estate: "text-orange-400 bg-orange-500/10",
  self_employment: "text-red-400 bg-red-500/10",
  filing_deadlines: "text-indigo-400 bg-indigo-500/10",
  strategies: "text-yellow-400 bg-yellow-500/10",
};

function CollapsibleSection({ sectionKey, section, children }) {
  const [open, setOpen] = useState(false);
  const Icon = SECTION_ICONS[sectionKey] || BookOpen;
  const colorClass = SECTION_COLORS[sectionKey] || "text-primary bg-primary/10";
  const [iconColor, bgColor] = colorClass.split(" ");

  return (
    <Card className="bg-[#0A0A0A] border-white/10 rounded-none overflow-hidden">
      <button
        data-testid={`section-${sectionKey}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-5 hover:bg-white/[0.02] transition-colors text-left"
      >
        <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white">{section.title}</h3>
          {section.description && (
            <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{section.description}</p>
          )}
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && <div className="border-t border-white/5 p-5">{children}</div>}
    </Card>
  );
}

function BracketTable({ rates, filingStatus }) {
  const key = filingStatus.replace(/ /g, "_");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-2 px-3 text-xs font-mono uppercase text-zinc-500">Rate</th>
            <th className="text-right py-2 px-3 text-xs font-mono uppercase text-zinc-500">Income Range</th>
          </tr>
        </thead>
        <tbody>
          {rates.map((b, i) => {
            const range = b[key] || b.single;
            return (
              <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-2.5 px-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                    b.rate <= 12 ? "bg-emerald-500/20 text-emerald-400" :
                    b.rate <= 24 ? "bg-amber-500/20 text-amber-400" :
                    "bg-red-500/20 text-red-400"
                  }`}>
                    {b.rate}%
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right font-mono text-zinc-300">
                  {formatCurrency(range[0])} — {range[1] ? formatCurrency(range[1]) : "No limit"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function QuickEstimator({ api }) {
  const [income, setIncome] = useState("");
  const [filing, setFiling] = useState("single");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    if (!income || Number(income) <= 0) return toast.error("Enter a valid income");
    setLoading(true);
    try {
      const res = await api.get(`/tax-education/quick-estimate?income=${income}&filing_status=${filing}`);
      setResult(res.data);
    } catch {
      toast.error("Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-[#0A0A0A] border-primary/30 rounded-none">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary" />
          Quick Tax Estimator
        </CardTitle>
        <p className="text-xs text-zinc-500">Estimate your 2026 federal tax using standard deduction only</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="text-xs text-zinc-500 mb-1 block">Gross Income</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="estimator-income"
                type="number"
                placeholder="e.g. 85000"
                value={income}
                onChange={(e) => setIncome(e.target.value)}
                className="pl-9 bg-black/50 border-white/10 rounded-sm"
              />
            </div>
          </div>
          <div className="w-full sm:w-48">
            <label className="text-xs text-zinc-500 mb-1 block">Filing Status</label>
            <Select value={filing} onValueChange={setFiling}>
              <SelectTrigger data-testid="estimator-filing" className="bg-black/50 border-white/10 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="single">Single</SelectItem>
                <SelectItem value="married_jointly">Married (Joint)</SelectItem>
                <SelectItem value="married_separately">Married (Sep)</SelectItem>
                <SelectItem value="head_of_household">Head of Household</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              data-testid="estimator-calculate"
              onClick={calculate}
              disabled={loading}
              className="bg-primary text-black font-bold rounded-sm hover:bg-primary/90 w-full sm:w-auto"
            >
              {loading ? "..." : "Calculate"}
            </Button>
          </div>
        </div>

        {result && (
          <div data-testid="estimator-result" className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-white/10">
            <div className="p-3 bg-white/[0.02] rounded">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Taxable Income</p>
              <p className="text-lg font-bold text-white font-mono">{formatCurrency(result.taxable_income)}</p>
            </div>
            <div className="p-3 bg-white/[0.02] rounded">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Estimated Tax</p>
              <p className="text-lg font-bold text-red-400 font-mono">{formatCurrency(result.estimated_tax)}</p>
            </div>
            <div className="p-3 bg-white/[0.02] rounded">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Effective Rate</p>
              <p className="text-lg font-bold text-amber-400 font-mono">{result.effective_rate}%</p>
            </div>
            <div className="p-3 bg-white/[0.02] rounded">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Marginal Rate</p>
              <p className="text-lg font-bold text-blue-400 font-mono">{result.marginal_rate}%</p>
            </div>
            {result.breakdown.length > 0 && (
              <div className="col-span-full mt-2">
                <p className="text-xs text-zinc-500 mb-2">Bracket Breakdown:</p>
                <div className="flex gap-1 h-6">
                  {result.breakdown.map((b, i) => {
                    const width = result.taxable_income > 0 ? (b.taxed_amount / result.taxable_income) * 100 : 0;
                    const colors = ["bg-emerald-500", "bg-emerald-600", "bg-amber-500", "bg-amber-600", "bg-orange-500", "bg-red-500", "bg-red-600"];
                    return (
                      <div
                        key={i}
                        className={`${colors[i] || "bg-zinc-500"} rounded-sm flex items-center justify-center transition-all`}
                        style={{ width: `${Math.max(width, 3)}%` }}
                        title={`${b.rate}%: ${formatCurrency(b.taxed_amount)} = ${formatCurrency(b.tax)} tax`}
                      >
                        <span className="text-[9px] text-black font-bold">{b.rate}%</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-zinc-600 mt-1 italic">{result.note}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function TaxEducation() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [bracketFiling, setBracketFiling] = useState("single");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get("/tax-education");
      setData(res.data);
    } catch {
      toast.error("Failed to load tax education data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Tax Education">
        <div className="flex items-center justify-center h-64">
          <div className="text-zinc-500">Loading IRS tax codes...</div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout title="Tax Education">
        <div className="text-center py-12 text-zinc-500">Failed to load data. Please refresh.</div>
      </Layout>
    );
  }

  const s = data.sections;
  const search = searchTerm.toLowerCase();

  const matchesSearch = (text) => !search || (text || "").toLowerCase().includes(search);

  return (
    <Layout title="Tax Education">
      <div data-testid="tax-education-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <p className="text-sm text-zinc-400">
              Tax Year <span className="text-primary font-bold">{data.tax_year}</span> | Source: {data.source}
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="tax-search"
              placeholder="Search tax topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-black/50 border-white/10 rounded-sm"
            />
          </div>
        </div>

        {/* Quick Estimator */}
        <QuickEstimator api={api} />

        {/* Tax Brackets */}
        {matchesSearch("bracket rate income") && (
          <CollapsibleSection sectionKey="brackets" section={s.brackets}>
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-3">
                <label className="text-xs text-zinc-500">Filing Status:</label>
                <Select value={bracketFiling} onValueChange={setBracketFiling}>
                  <SelectTrigger data-testid="bracket-filing-select" className="w-48 bg-black/50 border-white/10 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married_jointly">Married (Joint)</SelectItem>
                    <SelectItem value="married_separately">Married (Sep)</SelectItem>
                    <SelectItem value="head_of_household">Head of Household</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <BracketTable rates={s.brackets.rates} filingStatus={bracketFiling} />
              <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/10 rounded">
                <p className="text-xs text-blue-300 flex items-start gap-2">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Tax brackets are marginal — only income within each range is taxed at that rate. For example, a single filer earning $60,000 pays 10% on the first $12,400, 12% on $12,401-$50,400, and 22% only on the remaining $9,600.
                </p>
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Standard Deduction */}
        {matchesSearch("standard deduction") && (
          <CollapsibleSection sectionKey="standard_deduction" section={s.standard_deduction}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(s.standard_deduction.amounts).map(([status, amount]) => (
                <div key={status} className="p-3 bg-white/[0.02] border border-white/5 rounded">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{status.replace(/_/g, " ")}</p>
                  <p className="text-xl font-bold text-emerald-400 font-mono mt-1">{formatCurrency(amount)}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-emerald-500/5 border border-emerald-500/10 rounded">
              <p className="text-xs text-emerald-300 flex items-start gap-2">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {s.standard_deduction.additional.note} Additional deduction for age 65+/blind: {formatCurrency(s.standard_deduction.additional.age_65_or_blind_single)} (single) / {formatCurrency(s.standard_deduction.additional.age_65_or_blind_married)} (married).
              </p>
            </div>
          </CollapsibleSection>
        )}

        {/* Capital Gains */}
        {matchesSearch("capital gains dividend investment") && (
          <CollapsibleSection sectionKey="capital_gains" section={s.capital_gains}>
            <div className="space-y-4">
              <BracketTable rates={s.capital_gains.long_term_rates} filingStatus={bracketFiling} />
              <div className="p-3 bg-purple-500/5 border border-purple-500/10 rounded">
                <p className="text-xs text-purple-300 font-medium mb-1">Net Investment Income Tax (NIIT)</p>
                <p className="text-xs text-zinc-400">{s.capital_gains.niit.description}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Thresholds: Single {formatCurrency(s.capital_gains.niit.thresholds.single)} | MFJ {formatCurrency(s.capital_gains.niit.thresholds.married_jointly)}
                </p>
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Tax Credits */}
        {matchesSearch("credit child earned income education saver vehicle") && (
          <CollapsibleSection sectionKey="credits" section={s.credits}>
            <div className="space-y-4">
              {s.credits.items.map((credit, i) => (
                <div key={i} className="p-4 bg-white/[0.02] border border-white/5 rounded space-y-2">
                  <h4 className="text-sm font-semibold text-white">{credit.name}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-zinc-500">Amount:</span>
                      <p className="text-amber-400 font-medium">{credit.amount}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Refundable:</span>
                      <p className="text-zinc-300">{credit.refundable}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Phaseout:</span>
                      <p className="text-zinc-300">{credit.phaseout}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 mt-2 p-2 bg-amber-500/5 rounded">
                    <Lightbulb className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-amber-300/80">{credit.tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Retirement Limits */}
        {matchesSearch("retirement 401k ira hsa sep simple contribution") && (
          <CollapsibleSection sectionKey="retirement" section={s.retirement}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-3 text-xs font-mono uppercase text-zinc-500">Account</th>
                    <th className="text-right py-2 px-3 text-xs font-mono uppercase text-zinc-500">Limit</th>
                    <th className="text-right py-2 px-3 text-xs font-mono uppercase text-zinc-500">Catch-Up</th>
                    <th className="text-right py-2 px-3 text-xs font-mono uppercase text-zinc-500">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {s.retirement.items.map((item, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-2.5 px-3 text-zinc-300 font-medium">{item.account}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-white">{formatCurrency(item.limit)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-zinc-400">
                        +{formatCurrency(item.catch_up_50_plus || item.catch_up_55_plus || 0)}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-primary font-bold">
                        {formatCurrency(item.total_with_catch_up)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 space-y-2">
              {s.retirement.tips.map((tip, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <Lightbulb className="w-3 h-3 text-pink-400 mt-0.5 flex-shrink-0" />
                  <p className="text-zinc-400">{tip}</p>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Itemized Deductions */}
        {matchesSearch("deduction salt mortgage charitable medical qbi") && (
          <CollapsibleSection sectionKey="deductions" section={s.deductions}>
            <div className="space-y-4">
              {s.deductions.items.map((item, i) => (
                <div key={i} className="p-4 bg-white/[0.02] border border-white/5 rounded space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-white">{item.name}</h4>
                    <span className="text-xs font-mono text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">{item.limit}</span>
                  </div>
                  <p className="text-xs text-zinc-400">{item.description}</p>
                  <div className="flex items-start gap-2 p-2 bg-cyan-500/5 rounded">
                    <Lightbulb className="w-3 h-3 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-cyan-300/80">{item.tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Real Estate Tax Strategies */}
        {matchesSearch("real estate 1031 depreciation passive rental bonus opportunity") && (
          <CollapsibleSection sectionKey="real_estate" section={s.real_estate}>
            <div className="space-y-4">
              {s.real_estate.items.map((item, i) => (
                <div key={i} className="p-4 bg-white/[0.02] border border-white/5 rounded space-y-2">
                  <h4 className="text-sm font-semibold text-white">{item.name}</h4>
                  <p className="text-xs text-zinc-400">{item.description}</p>
                  <div className="flex items-start gap-2 p-2 bg-orange-500/5 rounded">
                    <Lightbulb className="w-3 h-3 text-orange-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-orange-300/80">{item.tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Self-Employment Tax */}
        {matchesSearch("self employment se tax quarterly estimated") && (
          <CollapsibleSection sectionKey="self_employment" section={s.self_employment}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="p-3 bg-white/[0.02] border border-white/5 rounded">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Social Security</p>
                  <p className="text-lg font-bold text-white font-mono">{s.self_employment.rates.social_security.rate}%</p>
                  <p className="text-xs text-zinc-500">on first {formatCurrency(s.self_employment.rates.social_security.wage_base)}</p>
                </div>
                <div className="p-3 bg-white/[0.02] border border-white/5 rounded">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Medicare</p>
                  <p className="text-lg font-bold text-white font-mono">{s.self_employment.rates.medicare.rate}%</p>
                  <p className="text-xs text-zinc-500">on all net earnings</p>
                </div>
                <div className="p-3 bg-white/[0.02] border border-white/5 rounded">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Additional Medicare</p>
                  <p className="text-lg font-bold text-white font-mono">+{s.self_employment.rates.additional_medicare.rate}%</p>
                  <p className="text-xs text-zinc-500">above {formatCurrency(s.self_employment.rates.additional_medicare.threshold_single)}</p>
                </div>
              </div>
              <p className="text-xs text-zinc-400">{s.self_employment.deduction}</p>
              <div className="space-y-2">
                {s.self_employment.tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <Lightbulb className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-zinc-400">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Filing Deadlines */}
        {matchesSearch("deadline filing date due extension") && (
          <CollapsibleSection sectionKey="filing_deadlines" section={s.filing_deadlines}>
            <div className="space-y-1">
              {s.filing_deadlines.dates.map((d, i) => {
                const isPast = new Date(d.date + ", 2026") < new Date();
                return (
                  <div key={i} className={`flex items-center gap-4 p-3 rounded ${isPast ? "opacity-50" : "hover:bg-white/[0.02]"}`}>
                    <div className="flex items-center gap-2 w-44 flex-shrink-0">
                      <Clock className="w-4 h-4 text-indigo-400" />
                      <span className="text-sm font-mono text-indigo-300">{d.date}</span>
                    </div>
                    <ArrowRight className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                    <span className="text-sm text-zinc-300">{d.event}</span>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Tax Optimization Strategies */}
        {matchesSearch("strategy optimization harvest backdoor roth conversion charitable hsa") && (
          <CollapsibleSection sectionKey="strategies" section={s.strategies}>
            <div className="space-y-4">
              {s.strategies.items.map((item, i) => (
                <div key={i} className="p-4 bg-white/[0.02] border border-white/5 rounded space-y-2">
                  <h4 className="text-sm font-semibold text-white">{item.name}</h4>
                  <p className="text-xs text-zinc-400">{item.description}</p>
                  <div className="flex items-start gap-2 p-2 bg-yellow-500/5 rounded">
                    <Lightbulb className="w-3 h-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <p className="text-[11px] text-yellow-300/80">{item.tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Footer Disclaimer */}
        <div className="text-center py-6 border-t border-white/5">
          <p className="text-xs text-zinc-600">
            This information is for educational purposes only and does not constitute tax advice. Consult a qualified tax professional for your specific situation. Data sourced from IRS.gov and the One Big Beautiful Bill Act (2025).
          </p>
        </div>
      </div>
    </Layout>
  );
}
